import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    createNotificationCampaign,
    getNotificationCampaignReach,
    getNotificationCampaign,
    getNotificationCampaignRecipients,
    getNotificationCampaigns,
    resendNotificationCampaignGap,
} from "services/notificationCampaigns";

export const notificationCampaignKeys = {
    all: ["notification-campaigns"],
    list: (params) => [...notificationCampaignKeys.all, "list", params],
    reach: (target) => [...notificationCampaignKeys.all, "reach", target],
    detail: (campaignId) => [...notificationCampaignKeys.all, "detail", campaignId],
    recipients: (campaignId, params) => [
        ...notificationCampaignKeys.all,
        "recipients",
        campaignId,
        params,
    ],
};

export const useNotificationCampaignReach = (target, enabled) => {
    const query = useQuery({
        queryKey: notificationCampaignKeys.reach(target),
        queryFn: () => getNotificationCampaignReach(target),
        enabled,
        staleTime: 15_000,
    });
    return {
        reach: query.data ?? null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
    };
};

export const useNotificationCampaigns = (params) => {
    const query = useQuery({
        queryKey: notificationCampaignKeys.list(params),
        queryFn: () => getNotificationCampaigns(params),
        refetchInterval: (state) =>
            state.state.data?.results?.some((campaign) =>
                ["queued", "processing"].includes(campaign.status),
            )
                ? 3000
                : false,
    });
    return {
        campaigns: query.data?.results ?? [],
        count: query.data?.count ?? 0,
        totalPages: query.data?.numOfPages ?? 0,
        loading: query.isLoading,
        error: query.error,
    };
};

export const useNotificationCampaign = (campaignId) => {
    const query = useQuery({
        queryKey: notificationCampaignKeys.detail(campaignId),
        queryFn: () => getNotificationCampaign(campaignId),
        enabled: Boolean(campaignId),
        refetchInterval: (state) =>
            ["queued", "processing"].includes(state.state.data?.status) ? 3000 : false,
    });
    return {
        campaign: query.data ?? null,
        loading: query.isLoading,
        error: query.error,
    };
};

export const useNotificationCampaignRecipients = (campaignId, params, options = {}) => {
    const query = useQuery({
        queryKey: notificationCampaignKeys.recipients(campaignId, params),
        queryFn: () => getNotificationCampaignRecipients(campaignId, params),
        enabled: Boolean(campaignId),
        refetchInterval: options.poll ? 3000 : false,
    });
    return {
        recipients: query.data?.results ?? [],
        count: query.data?.count ?? 0,
        totalPages: query.data?.numOfPages ?? 0,
        loading: query.isLoading,
        error: query.error,
    };
};

export const useCreateNotificationCampaign = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createNotificationCampaign,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationCampaignKeys.all }),
    });
};

export const useResendNotificationCampaignGap = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: resendNotificationCampaignGap,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationCampaignKeys.all }),
    });
};
